/**
 * STAGE: Gemini reads each cut WhisperX made.
 *
 * WhisperX decided WHERE the cuts are; this stage asks Gemini what each one
 * actually says, one clip at a time, three at a time. Silence tiles are skipped
 * — WhisperX already decided they hold nothing, and re-asking would only invite
 * the model to hallucinate words into quiet.
 *
 * Mutates the tiles in place, which is what the caller wants: the timeline's
 * geometry is not up for discussion here, only the labels on it.
 *
 * Failure of ONE clip is not failure of the stage: the tile keeps WhisperX's
 * label and the pool moves on.
 */

import { isFillerSpelling } from '../../lib/labelText';
import { sliceToWav } from '../audioSlicer';
import { transcribeCut } from '../gemini';
import type { AlignedTile } from './alignedTiling';

/** How many clips are in flight at once. */
const POOL = 3;

export interface ReadCutsOptions {
  buffer: AudioBuffer;
  /** The tiles to re-read. MUTATED in place. */
  tiles: AlignedTile[];
  /**
   * FORCE mode: the filler tiles came from WhisperX's filler-biased pass, which
   * finds them reliably — so Gemini is not allowed to touch them. Letting it
   * relabel a filler is exactly how "ايه" kept disappearing: Gemini reads the
   * clip, tidies the hesitation away, and the tile becomes a word that was
   * never spoken there.
   */
  forceFillers: boolean;
  /** PROMPT mode: ask Gemini to pay attention to hesitations. */
  emphasizeFillers: boolean;
  signal?: AbortSignal;
  onPhase?: (message: string, frac: number) => void;
}

export async function readCuts(options: ReadCutsOptions): Promise<void> {
  const { buffer, tiles, forceFillers, emphasizeFillers, signal, onPhase } = options;
  onPhase?.('Gemini: reading each cut…', 0.75);
  const targets = tiles.filter((t) => t.kind !== 'silence' && !(forceFillers && t.kind === 'filler'));
  let done = 0;
  let next = 0;
  const worker = async (): Promise<void> => {
    for (;;) {
      const i = next++;
      if (i >= targets.length) return;
      const tile = targets[i];
      try {
        const clip = sliceToWav(buffer, tile.startSec, tile.endSec, { padSec: 0.02 });
        const answer = await transcribeCut({ audio: clip, mimeType: 'audio/wav', emphasizeFillers, signal });
        const text = answer.text.trim();
        if (text) {
          tile.heard = text;
          tile.contextual = text;
          tile.kind = answer.kind === 'silence' ? tile.kind : answer.kind;
          if (isFillerSpelling(text)) tile.kind = 'filler';
          tile.trace = [...(tile.trace ?? []), 'gemini:read-cut'];
        }
      } catch {
        // one clip failing must not lose the run: keep WhisperX's label
      }
      done++;
      onPhase?.(`Gemini: reading each cut… ${done}/${targets.length}`, 0.75 + (done / Math.max(1, targets.length)) * 0.2);
    }
  };
  await Promise.all(Array.from({ length: Math.min(POOL, targets.length) }, worker));
}
